import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Field, SelectField } from "@/components/common/Field";
import { STATUS_ORDER, statusMeta, LOST_REASONS } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { Loader2, Check } from "lucide-react";

export const ChangeStatusDialog = ({ open, onOpenChange, lead, onUpdated }) => {
  const [selected, setSelected] = useState(lead?.status || "NEW");
  const [reason, setReason] = useState(lead?.lost_reason || "");
  const [reasonNote, setReasonNote] = useState("");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (open) { setSelected(lead?.status || "NEW"); setReason(""); setReasonNote(""); }
  }, [open, lead]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { status: selected };
      if (selected === "LOST") {
        payload.lost_reason = [reason, reasonNote].filter(Boolean).join(" — ") || null;
      }
      const res = await api.patch(`/leads/${lead.id}/status`, payload);
      toast.success(`Status updated to ${statusMeta(selected).label}`);
      onUpdated?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update status");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Change Status</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          {STATUS_ORDER.map((s) => {
            const meta = statusMeta(s);
            const active = selected === s;
            return (
              <button key={s} onClick={() => setSelected(s)}
                className={cn(
                  "flex items-center justify-between rounded-lg border-2 px-3 py-2.5 text-sm font-semibold transition-colors",
                  active ? "border-brand bg-accent text-foreground" : "border-border bg-card text-muted-foreground hover:border-brand/40"
                )}>
                <span className="flex items-center gap-2">
                  <span className={cn("h-2 w-2 rounded-full", meta.dot)} /> {meta.label}
                </span>
                {active && <Check className="h-4 w-4 text-brand" />}
              </button>
            );
          })}
        </div>

        {selected === "LOST" && (
          <div className="space-y-3 rounded-lg bg-destructive/5 p-3">
            <Field label="Reason for loss">
              <SelectField value={reason} onChange={setReason} options={LOST_REASONS} placeholder="Select a reason" />
            </Field>
            <Field label="Remarks">
              <Textarea rows={2} value={reasonNote} onChange={(e) => setReasonNote(e.target.value)} placeholder="Optional details" />
            </Field>
          </div>
        )}
        {selected === "DEAL_DONE" && (
          <p className="rounded-lg bg-success/10 p-3 text-xs text-success">
            Marking Deal Done opens the Deal Processing workflow (Documents → Approval → Payment → COD). The transaction is only complete once COD is issued.
          </p>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button variant="brand" onClick={submit} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Update Status
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
